// The presentation deck's pure HTML core: the slide/theme types and the
// JSON->HTML renderer both apps draw the deck from (bluesky's full viewer +
// PDF export; chief's inline srcdoc iframe). No DOM, no network.

export type Theme =
  | "professional"
  | "modern"
  | "minimal"
  | "dark"
  | "vibrant"
  | "executive"
  | "startup"
  | "tech"
  | "finance"
  | "gradient"
  | "neon"
  | "pastel"
  | "nature";
export type SlideType =
  | "title-slide"
  | "content-slide"
  | "image-slide"
  | "two-column"
  | "section-header"
  | "chart-slide"
  | "timeline-slide"
  | "comparison-slide"
  | "kpi-dashboard";
type ContentItemType = "bullet" | "paragraph" | "heading" | "code-block" | "quote" | "table" | "kpi" | "icon";

export interface PresentationData {
  type: "presentation";
  storageLocation?: { bucket: string; key: string };
  metadata?: { title: string; slideCount: number; theme: Theme };
  presentation?: Presentation;
}

export interface Presentation {
  version: string;
  title: string;
  theme?: Theme;
  slides: Slide[];
}

export interface Slide {
  id: string;
  type: SlideType;
  title?: string;
  subtitle?: string;
  content?: ContentItem[];
  leftColumn?: Column;
  rightColumn?: Column;
  image?: ImageRef;
  chart?: ChartRef;
  speakerNotes?: string;
  timeline?: Timeline;
  comparison?: Comparison;
  kpis?: KPICard[];
}

export interface ContentItem {
  type: ContentItemType;
  text: string;
  level?: number;
  codeBlock?: CodeBlock;
  quote?: Quote;
  kpi?: KPICard;
  table?: Table;
  icon?: Icon;
}

export interface Column {
  heading?: string;
  content: ContentItem[];
}

export interface ImageRef {
  type: "artifact";
  artifactId?: string;
  url?: string;
  alt?: string;
  caption?: string;
}

export interface ChartRef {
  type: "artifact";
  htmlArtifactId?: string;
  pngArtifactId?: string;
  url?: string;
  alt?: string;
  caption?: string;
}

interface CodeBlock {
  language?: string;
  code: string;
  title?: string;
  showLineNumbers?: boolean;
}

interface Quote {
  text: string;
  attribution?: string;
  style?: "default" | "info" | "warning" | "success" | "tip";
  title?: string;
}

export interface KPITrend {
  direction: "up" | "down" | "neutral";
  change?: string;
  isPositive?: boolean;
}

export interface KPICard {
  value: string;
  label: string;
  trend?: KPITrend;
  comparisonText?: string;
  icon?: string;
}

interface Table {
  headers?: string[];
  rows: string[][];
  caption?: string;
}

interface Icon {
  char: string;
  label?: string;
  size?: "small" | "medium" | "large";
}

interface TimelineStep {
  title: string;
  description?: string;
  date?: string;
  status?: "completed" | "active" | "pending";
}

export interface Timeline {
  steps: TimelineStep[];
  direction?: "horizontal" | "vertical";
}

export interface ComparisonItem {
  title: string;
  points: string[];
  style?: "positive" | "negative" | "neutral";
}

export interface Comparison {
  left: ComparisonItem;
  right: ComparisonItem;
  versusLabel?: string;
}

function escapeHTML(str: string | undefined | null): string {
  if (str == null) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// `page` fills the rest of a continuation page, which the slide's own background doesn't reach.
const THEME_CSS: Record<Theme, { slide: string; bullet: string; page: string }> = {
  professional: {
    slide: "background: white; color: #111827;",
    bullet: "background: #111827;",
    page: "white",
  },
  modern: {
    slide: "background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%); color: #0f172a;",
    bullet: "background: #0f172a;",
    page: "#eff6ff",
  },
  minimal: {
    slide: "background: #f9fafb; color: #1f2937;",
    bullet: "background: #1f2937;",
    page: "#f9fafb",
  },
  dark: {
    slide: "background: #111827; color: white;",
    bullet: "background: white;",
    page: "#111827",
  },
  vibrant: {
    slide: "background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%); color: white;",
    bullet: "background: white;",
    page: "#ec4899",
  },
  executive: {
    slide: "background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); color: white;",
    bullet: "background: #94a3b8;",
    page: "#0f172a",
  },
  startup: {
    slide: "background: linear-gradient(135deg, #6366f1 0%, #9333ea 100%); color: white;",
    bullet: "background: white;",
    page: "#9333ea",
  },
  tech: {
    slide: "background: linear-gradient(135deg, #164e63 0%, #0f172a 100%); color: #ecfeff;",
    bullet: "background: #22d3ee;",
    page: "#0f172a",
  },
  finance: {
    slide: "background: linear-gradient(135deg, #065f46 0%, #0f172a 100%); color: white;",
    bullet: "background: #34d399;",
    page: "#0f172a",
  },
  gradient: {
    slide: "background: linear-gradient(135deg, #fb7185 0%, #d946ef 50%, #6366f1 100%); color: white;",
    bullet: "background: white;",
    page: "#6366f1",
  },
  neon: {
    slide: "background: #000000; color: #4ade80;",
    bullet: "background: #4ade80;",
    page: "#000000",
  },
  pastel: {
    slide: "background: linear-gradient(135deg, #fce7f3 0%, #dbeafe 100%); color: #1e293b;",
    bullet: "background: #64748b;",
    page: "#dbeafe",
  },
  nature: {
    slide: "background: linear-gradient(135deg, #15803d 0%, #064e3b 100%); color: white;",
    bullet: "background: #86efac;",
    page: "#064e3b",
  },
};

const getQuoteBorderColor = (style?: "default" | "info" | "warning" | "success" | "tip"): string => {
  switch (style) {
    case "info":
      return "#3b82f6";
    case "warning":
      return "#f59e0b";
    case "success":
      return "#22c55e";
    case "tip":
      return "#a855f7";
    default:
      return "currentColor";
  }
};

const getStatusColor = (status?: "completed" | "active" | "pending"): string => {
  switch (status) {
    case "completed":
      return "#22c55e";
    case "active":
      return "#3b82f6";
    default:
      return "currentColor";
  }
};

const getStyleBorder = (style?: "positive" | "negative" | "neutral"): string => {
  switch (style) {
    case "positive":
      return "border-color: rgba(34, 197, 94, 0.3); background: rgba(34, 197, 94, 0.05);";
    case "negative":
      return "border-color: rgba(239, 68, 68, 0.3); background: rgba(239, 68, 68, 0.05);";
    default:
      return "border-color: color-mix(in srgb, currentColor 20%, transparent); background: rgba(0,0,0,0.05);";
  }
};

const getTrendArrow = (direction?: "up" | "down" | "neutral"): string => {
  switch (direction) {
    case "up":
      return "+";
    case "down":
      return "-";
    default:
      return "";
  }
};

const getTrendColor = (trend?: KPITrend): string => {
  if (!trend) return "currentColor";
  if (trend.isPositive === true) return "#22c55e";
  if (trend.isPositive === false) return "#ef4444";
  switch (trend.direction) {
    case "up":
      return "#22c55e";
    case "down":
      return "#ef4444";
    default:
      return "currentColor";
  }
};

export function presentationToHTML(presentation: Presentation, theme: Theme): string {
  const themeCSS = THEME_CSS[theme] || THEME_CSS.professional;

  const renderContentItem = (item: ContentItem): string => {
    const indent = (item.level ?? 0) * 24;
    switch (item.type) {
      case "bullet":
        return `<li style="display: flex; align-items: flex-start; gap: 12px; font-size: 20px; margin-left: ${indent}px; margin-bottom: 16px;">
          <span style="margin-top: 8px; width: 8px; height: 8px; border-radius: 50%; ${themeCSS.bullet} flex-shrink: 0; opacity: 0.6;"></span>
          <span>${escapeHTML(item.text)}</span>
        </li>`;
      case "paragraph":
        return `<p style="font-size: 20px; line-height: 1.625; margin-bottom: 16px;">${escapeHTML(item.text)}</p>`;
      case "heading":
        return `<h3 style="font-size: 24px; font-weight: 600; margin-top: 24px; margin-bottom: 16px;">${escapeHTML(item.text)}</h3>`;
      case "code-block":
        if (!item.codeBlock) return "";
        return `<div style="border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
          ${item.codeBlock.title ? `<div style="padding: 8px 16px; background: #1f2937; color: #d1d5db; font-size: 14px; font-weight: 500; border-bottom: 1px solid #374151;">${escapeHTML(item.codeBlock.title)}</div>` : ""}
          <pre style="margin: 0; padding: 16px; background: #111827; color: #f3f4f6; overflow-x: auto;"><code style="font-family: monospace; font-size: 14px; white-space: pre;">${escapeHTML(item.codeBlock.code)}</code></pre>
        </div>`;
      case "quote":
        if (!item.quote) return "";
        return `<blockquote style="padding: 12px 16px; margin: 0 0 16px 0; border-left: 4px solid ${getQuoteBorderColor(item.quote.style)}; background: rgba(0,0,0,0.05); border-radius: 0 8px 8px 0;">
          ${item.quote.title ? `<div style="font-weight: 600; margin-bottom: 8px;">${escapeHTML(item.quote.title)}</div>` : ""}
          <p style="font-size: 18px; font-style: italic; margin: 0;">${escapeHTML(item.quote.text)}</p>
          ${item.quote.attribution ? `<cite style="display: block; margin-top: 8px; font-size: 14px; opacity: 0.7; font-style: normal;">- ${escapeHTML(item.quote.attribution)}</cite>` : ""}
        </blockquote>`;
      case "table":
        if (!item.table) return "";
        return `<div style="overflow-x: auto; margin-bottom: 16px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 18px;">
            ${item.table.headers && item.table.headers.length > 0 ? `<thead><tr style="border-bottom: 2px solid color-mix(in srgb, currentColor 30%, transparent);">${item.table.headers.map((h) => `<th style="padding: 8px 16px; text-align: left; font-weight: 600;">${escapeHTML(h)}</th>`).join("")}</tr></thead>` : ""}
            <tbody>${item.table.rows.map((row) => `<tr style="border-bottom: 1px solid color-mix(in srgb, currentColor 10%, transparent);">${row.map((cell) => `<td style="padding: 8px 16px;">${escapeHTML(cell)}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
          ${item.table.caption ? `<p style="margin-top: 8px; font-size: 14px; opacity: 0.7; text-align: center;">${escapeHTML(item.table.caption)}</p>` : ""}
        </div>`;
      case "kpi":
        if (!item.kpi) return "";
        return `<div style="display: inline-flex; align-items: center; gap: 16px; padding: 16px; border: 1px solid color-mix(in srgb, currentColor 20%, transparent); border-radius: 8px; background: rgba(0,0,0,0.05); margin-bottom: 16px;">
          <div>
            <div style="font-size: 32px; font-weight: bold;">${escapeHTML(item.kpi.value)}</div>
            <div style="font-size: 14px; opacity: 0.8;">${escapeHTML(item.kpi.label)}</div>
          </div>
        </div>`;
      case "icon":
        return "";
      default:
        return `<p>${escapeHTML(item.text)}</p>`;
    }
  };

  const renderSlide = (slide: Slide, index: number): string => {
    const pageBreak = index < presentation.slides.length - 1 ? "page-break-after: always;" : "";
    // Headless Chrome drops the background of a fixed-height slide that overflows
    // and paints the spill over the next slide; clipping would lose content the
    // PDF can't scroll to. So an overflowing slide grows onto a continuation page.
    const baseStyle = `${themeCSS.slide} width: 100%; min-height: 100vh; padding: 48px; box-sizing: border-box; box-decoration-break: clone; position: relative; ${pageBreak}`;

    switch (slide.type) {
      case "title-slide":
      case "section-header":
        return `<div style="${baseStyle} display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;">
          <h1 style="font-size: 48px; font-weight: bold; margin-bottom: 24px; line-height: 1.2;">${escapeHTML(slide.title || "")}</h1>
          ${slide.subtitle ? `<p style="font-size: 24px; opacity: 0.8;">${escapeHTML(slide.subtitle)}</p>` : ""}
        </div>`;

      case "content-slide":
        return `<div style="${baseStyle} display: flex; flex-direction: column;">
          <h2 style="font-size: 32px; font-weight: bold; margin-bottom: 32px;">${escapeHTML(slide.title || "")}</h2>
          <div style="list-style: none; padding: 0; margin: 0;">
            ${(slide.content || []).map(renderContentItem).join("")}
          </div>
        </div>`;

      case "image-slide": {
        const imageUrl = slide.image?.url;
        return `<div style="${baseStyle} display: flex; flex-direction: column; align-items: center; justify-content: center;">
          ${slide.title ? `<h2 style="font-size: 24px; font-weight: bold; margin-bottom: 24px;">${escapeHTML(slide.title)}</h2>` : ""}
          ${imageUrl ? `<img src="${escapeHTML(imageUrl)}" alt="${escapeHTML(slide.image?.alt || "")}" style="max-height: 70%; max-width: 100%; object-fit: contain; border-radius: 8px;" />` : ""}
          ${slide.image?.caption ? `<p style="margin-top: 16px; font-size: 18px; opacity: 0.7;">${escapeHTML(slide.image.caption)}</p>` : ""}
        </div>`;
      }

      case "chart-slide": {
        const chartUrl = slide.chart?.url;
        return `<div style="${baseStyle} display: flex; flex-direction: column; align-items: center; justify-content: center;">
          ${slide.title ? `<h2 style="font-size: 24px; font-weight: bold; margin-bottom: 24px;">${escapeHTML(slide.title)}</h2>` : ""}
          ${chartUrl ? `<img src="${escapeHTML(chartUrl)}" alt="${escapeHTML(slide.chart?.alt || "Chart")}" style="max-height: 70%; max-width: 100%; object-fit: contain; border-radius: 8px;" />` : ""}
          ${slide.chart?.caption ? `<p style="margin-top: 16px; font-size: 18px; opacity: 0.7;">${escapeHTML(slide.chart.caption)}</p>` : ""}
        </div>`;
      }

      case "two-column": {
        const renderColumn = (column: Column | undefined): string => {
          if (!column) return "";
          return `<div style="flex: 1;">
            ${column.heading ? `<h3 style="font-size: 20px; font-weight: 600; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid color-mix(in srgb, currentColor 20%, transparent);">${escapeHTML(column.heading)}</h3>` : ""}
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${(column.content || [])
                .map(
                  (
                    item
                  ) => `<li style="display: flex; align-items: flex-start; gap: 8px; font-size: 18px; margin-bottom: 12px;">
                <span style="margin-top: 8px; width: 6px; height: 6px; border-radius: 50%; ${themeCSS.bullet} flex-shrink: 0; opacity: 0.6;"></span>
                <span>${escapeHTML(item.text)}</span>
              </li>`
                )
                .join("")}
            </ul>
          </div>`;
        };
        return `<div style="${baseStyle} display: flex; flex-direction: column;">
          <h2 style="font-size: 32px; font-weight: bold; margin-bottom: 32px;">${escapeHTML(slide.title || "")}</h2>
          <div style="flex: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 32px;">
            ${renderColumn(slide.leftColumn)}
            ${renderColumn(slide.rightColumn)}
          </div>
        </div>`;
      }

      case "timeline-slide": {
        const steps = slide.timeline?.steps || [];
        const stepsHTML = steps
          .map(
            (step, i) => `
          <div style="display: flex; align-items: flex-start;">
            <div style="display: flex; flex-direction: column; align-items: center; min-width: 128px;">
              <div style="width: 32px; height: 32px; border-radius: 50%; border: 2px solid ${getStatusColor(step.status)}; background: ${step.status === "completed" || step.status === "active" ? getStatusColor(step.status) : "transparent"}; display: flex; align-items: center; justify-content: center; font-size: 14px;">
                <span style="font-size: 12px;">${i + 1}</span>
              </div>
              <div style="margin-top: 12px; text-align: center;">
                <div style="font-weight: 600; font-size: 18px;">${escapeHTML(step.title)}</div>
                ${step.date ? `<div style="font-size: 14px; opacity: 0.6; margin-top: 4px;">${escapeHTML(step.date)}</div>` : ""}
                ${step.description ? `<div style="font-size: 14px; opacity: 0.8; margin-top: 8px; max-width: 128px;">${escapeHTML(step.description)}</div>` : ""}
              </div>
            </div>
            ${i < steps.length - 1 ? `<div style="height: 2px; width: 64px; background: currentColor; opacity: 0.3; margin-top: 16px;"></div>` : ""}
          </div>`
          )
          .join("");
        return `<div style="${baseStyle} display: flex; flex-direction: column;">
          ${slide.title ? `<h2 style="font-size: 32px; font-weight: bold; margin-bottom: 32px;">${escapeHTML(slide.title)}</h2>` : ""}
          <div style="flex: 1; display: flex; align-items: center; justify-content: center; overflow: auto;">
            <div style="display: flex; align-items: flex-start; gap: 16px;">
              ${stepsHTML}
            </div>
          </div>
        </div>`;
      }

      case "comparison-slide": {
        const comparison = slide.comparison;
        const renderComparisonColumn = (item: ComparisonItem | undefined): string => {
          if (!item) return "";
          return `<div style="flex: 1; padding: 24px; border-radius: 8px; border: 1px solid; ${getStyleBorder(item.style)}">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 16px;">
              <h3 style="font-size: 20px; font-weight: 600; margin: 0;">${escapeHTML(item.title)}</h3>
            </div>
            <ul style="list-style: none; padding: 0; margin: 0;">
              ${item.points
                .map(
                  (
                    point
                  ) => `<li style="display: flex; align-items: flex-start; gap: 8px; font-size: 18px; margin-bottom: 12px;">
                <span style="margin-top: 8px; width: 6px; height: 6px; border-radius: 50%; ${themeCSS.bullet} flex-shrink: 0; opacity: 0.6;"></span>
                <span>${escapeHTML(point)}</span>
              </li>`
                )
                .join("")}
            </ul>
          </div>`;
        };
        return `<div style="${baseStyle} display: flex; flex-direction: column;">
          ${slide.title ? `<h2 style="font-size: 32px; font-weight: bold; margin-bottom: 32px;">${escapeHTML(slide.title)}</h2>` : ""}
          <div style="flex: 1; display: flex; align-items: center; gap: 24px;">
            ${renderComparisonColumn(comparison?.left)}
            ${comparison?.versusLabel ? `<div style="flex-shrink: 0; padding: 8px 16px; border-radius: 9999px; background: rgba(0,0,0,0.1); font-weight: 600; font-size: 14px;">${escapeHTML(comparison.versusLabel)}</div>` : ""}
            ${renderComparisonColumn(comparison?.right)}
          </div>
        </div>`;
      }

      case "kpi-dashboard": {
        const kpis = slide.kpis || [];
        const kpisHTML = kpis
          .map(
            (kpi) => `
          <div style="padding: 24px; border-radius: 8px; border: 1px solid color-mix(in srgb, currentColor 20%, transparent); background: rgba(0,0,0,0.05);">
            <div style="font-size: 40px; font-weight: bold; margin-bottom: 8px;">${escapeHTML(kpi.value)}</div>
            <div style="font-size: 18px; opacity: 0.8;">${escapeHTML(kpi.label)}</div>
            ${kpi.trend ? `<div style="display: flex; align-items: center; gap: 4px; margin-top: 8px; font-size: 14px; color: ${getTrendColor(kpi.trend)};"><span>${getTrendArrow(kpi.trend.direction)}</span>${kpi.trend.change ? `<span>${escapeHTML(kpi.trend.change)}</span>` : ""}</div>` : ""}
            ${kpi.comparisonText ? `<div style="font-size: 12px; opacity: 0.6; margin-top: 4px;">${escapeHTML(kpi.comparisonText)}</div>` : ""}
          </div>`
          )
          .join("");
        return `<div style="${baseStyle} display: flex; flex-direction: column;">
          ${slide.title ? `<h2 style="font-size: 32px; font-weight: bold; margin-bottom: 32px;">${escapeHTML(slide.title)}</h2>` : ""}
          <div style="flex: 1; display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; align-content: center;">
            ${kpisHTML}
          </div>
        </div>`;
      }

      default:
        return `<div style="${baseStyle}"><p>${escapeHTML(slide.title || "")}</p></div>`;
    }
  };

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${escapeHTML(presentation.title || "Presentation")}</title>
  <style>
    @page { size: landscape; margin: 0; }
    html { background: ${themeCSS.page}; }
    html, body { height: 100%; margin: 0; padding: 0; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  </style>
</head>
<body>
  ${presentation.slides.map((slide, i) => renderSlide(slide, i)).join("")}
</body>
</html>`;
}
